/**
 * Czytelnicy obietnic cennika z migracji 861 (plan 56, etap 4):
 *
 *  - `ZmianyCennikaKlienta` — rozliczenia organizacji: zawiadomienia
 *    o zmianie cen (kwota przed/po, data, przyczyna) i wypowiedzenie do dnia
 *    obowiązywania (Regulamin §11, §13). Zapis wypowiedzenia robi RLS
 *    (właściciel albo administrator organizacji z zawiadomieniem), chwilę
 *    i osobę — wyzwalacz z sesji;
 *  - `PublikacjaZmianyCennika` — super-admin publikuje zmianę pozycji
 *    cennika modułów (`rodzaj="modul"`) albo planów (`rodzaj="plan"`); datę
 *    ≥ 60 dni, 12 miesięcy i warunek klauzuli kursowej pilnuje baza, ekran
 *    pokazuje jej zdanie odmowy;
 *  - `LicznikPoziomuBezplatnego` — konta bezpłatne wobec sufitu (Regulamin §3).
 *
 * Żaden z nich nie liczy reguł sam: bramki są w bazie, ekran je czyta.
 */

import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { supabase } from '../../lib/supabase';

export const PRZYCZYNY_ZMIANY: Record<string, string> = {
  koszty_dostawcow: 'Wzrost kosztów dostawców',
  zmiana_zakresu_uslugi: 'Zmiana zakresu usługi',
  zmiana_prawa_lub_podatkow: 'Zmiana prawa lub podatków',
  inflacja: 'Inflacja',
  klauzula_kursowa: 'Klauzula kursowa (kurs NBP USD)',
};

export interface ZmianaCennika {
  id: string;
  module_price_id: string | null;
  plan_price_id: string | null;
  kwota_przed: number | null;
  kwota_po: number;
  waluta: string | null;
  przyczyna: string;
  uzasadnienie: string;
  opublikowano_at: string;
  obowiazuje_od: string;
  wycofano_at: string | null;
  zastosowano_at: string | null;
}

export function kwotaZl(k: number | null, waluta: string | null): string {
  if (k === null || k === undefined) return '—';
  return `${Number(k).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${waluta ?? 'PLN'}`;
}

export function dzien(iso: string): string {
  return new Date(iso).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' });
}

/** Czy wypowiedzenie jest jeszcze możliwe — do dnia obowiązywania zmiany. */
export function moznaWypowiedziec(
  z: Pick<ZmianaCennika, 'obowiazuje_od' | 'wycofano_at'>,
  teraz = new Date()
): boolean {
  return !z.wycofano_at && teraz < new Date(z.obowiazuje_od);
}

export function ZmianyCennikaKlienta({
  organizationId,
  mozeWypowiedziec,
}: {
  organizationId: string;
  mozeWypowiedziec: boolean;
}) {
  const [zmiany, setZmiany] = useState<ZmianaCennika[]>([]);
  const [wypowiedziane, setWypowiedziane] = useState<Set<string>>(new Set());
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState<string | null>(null);

  const czytaj = useCallback(async () => {
    const [p, w] = await Promise.all([
      supabase
        .from('cennik_zmiany_powiadomienia')
        .select(
          'zmiana_id, cennik_zmiany(id, module_price_id, plan_price_id, kwota_przed, kwota_po, waluta, przyczyna, uzasadnienie, opublikowano_at, obowiazuje_od, wycofano_at, zastosowano_at)'
        )
        .eq('organization_id', organizationId),
      supabase
        .from('cennik_wypowiedzenia')
        .select('zmiana_id')
        .eq('organization_id', organizationId),
    ]);
    if (p.error) {
      setBlad(p.error.message);
      return;
    }
    const lista = (
      (p.data ?? []) as Array<{ cennik_zmiany: ZmianaCennika | ZmianaCennika[] | null }>
    )
      .map((r) => (Array.isArray(r.cennik_zmiany) ? r.cennik_zmiany[0] : r.cennik_zmiany))
      .filter((z): z is ZmianaCennika => !!z && !z.wycofano_at);
    setZmiany(lista);
    setWypowiedziane(
      new Set(((w.data ?? []) as Array<{ zmiana_id: string }>).map((r) => r.zmiana_id))
    );
    setBlad(null);
  }, [organizationId]);

  useEffect(() => {
    void czytaj();
  }, [czytaj]);

  const wypowiedz = async (zmianaId: string) => {
    setZapis(zmianaId);
    const { error } = await supabase
      .from('cennik_wypowiedzenia')
      .insert({ zmiana_id: zmianaId, organization_id: organizationId });
    setZapis(null);
    if (error) setBlad(error.message);
    else await czytaj();
  };

  if (blad) return <div className="card p-4 text-sm text-red-400">Zmiany cennika: {blad}</div>;
  if (zmiany.length === 0) return null;

  return (
    <section className="card p-4 space-y-3" aria-label="Zmiany cennika">
      <h2 className="card__title">Zmiany cennika</h2>
      {zmiany.map((z) => (
        <div key={z.id} className="border-t border-aurora-border pt-3 text-sm space-y-1">
          <p>
            <span className="mono">{kwotaZl(z.kwota_przed, z.waluta)}</span> →{' '}
            <span className="mono">{kwotaZl(z.kwota_po, z.waluta)}</span> netto od{' '}
            <span className="mono">{dzien(z.obowiazuje_od)}</span> ·{' '}
            {PRZYCZYNY_ZMIANY[z.przyczyna] ?? z.przyczyna}
          </p>
          <p className="text-aurora-muted">{z.uzasadnienie}</p>
          {wypowiedziane.has(z.id) ? (
            <p className="text-amber-400">
              Wypowiedzenie złożone — zespół rozliczy zwrot za niewykorzystany okres.
            </p>
          ) : moznaWypowiedziec(z) && mozeWypowiedziec ? (
            <button
              className="btn btn--secondary"
              disabled={zapis === z.id}
              onClick={() => void wypowiedz(z.id)}
            >
              {zapis === z.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Wypowiadam umowę przed zmianą
            </button>
          ) : moznaWypowiedziec(z) ? (
            <p className="text-aurora-muted">
              Wypowiedzenie może złożyć właściciel albo administrator organizacji.
            </p>
          ) : null}
        </div>
      ))}
    </section>
  );
}

interface PozycjaModulu {
  id: string;
  module_id: string;
  billing_type: string;
  amount: number;
  currency: string;
  module_pricing_tiers: { slug: string } | { slug: string }[] | null;
}

interface PozycjaPlanu {
  id: string;
  plan: string;
  billing_interval: string;
  amount: number;
  currency: string;
}

/** Pozycja na liście wyboru — wspólny kształt cennika modułów i planów. */
export interface PozycjaCennika {
  id: string;
  opis: string;
  amount: number;
  currency: string;
}

export type RodzajCennika = 'modul' | 'plan';

function slug(p: PozycjaModulu['module_pricing_tiers']): string {
  const w = Array.isArray(p) ? p[0] : p;
  return w?.slug ?? '?';
}

/** Kolumna `cennik_zmiany`, która wskazuje pozycję danego rodzaju. */
export function kolumnaPozycji(rodzaj: RodzajCennika): 'module_price_id' | 'plan_price_id' {
  return rodzaj === 'plan' ? 'plan_price_id' : 'module_price_id';
}

async function pozycjeCennika(rodzaj: RodzajCennika): Promise<PozycjaCennika[]> {
  if (rodzaj === 'plan') {
    const { data } = await supabase
      .from('plan_prices')
      .select('id, plan, billing_interval, amount, currency')
      .eq('is_active', true)
      .order('plan');
    return ((data ?? []) as PozycjaPlanu[]).map((p) => ({
      id: p.id,
      opis: `${p.plan} · ${p.billing_interval}`,
      amount: p.amount,
      currency: p.currency,
    }));
  }
  const { data } = await supabase
    .from('module_prices')
    .select('id, module_id, billing_type, amount, currency, module_pricing_tiers(slug)')
    .eq('is_active', true)
    .order('module_id');
  return ((data ?? []) as PozycjaModulu[]).map((p) => ({
    id: p.id,
    opis: `${p.module_id} · ${slug(p.module_pricing_tiers)} · ${p.billing_type}`,
    amount: p.amount,
    currency: p.currency,
  }));
}

/** Domyślna data obowiązywania: 62 dni od dziś (bramka liczy 60 od wysyłki). */
export function domyslnaDataObowiazywania(teraz = new Date()): string {
  const d = new Date(teraz.getTime() + 62 * 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

export function PublikacjaZmianyCennika({ rodzaj = 'modul' }: { rodzaj?: RodzajCennika }) {
  const [pozycje, setPozycje] = useState<PozycjaCennika[]>([]);
  const [zmiany, setZmiany] = useState<ZmianaCennika[]>([]);
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);
  const [form, setForm] = useState({
    pozycjaId: '',
    kwota: '',
    przyczyna: 'koszty_dostawcow',
    uzasadnienie: '',
    obowiazujeOd: domyslnaDataObowiazywania(),
  });

  const czytaj = useCallback(async () => {
    const [p, z] = await Promise.all([
      pozycjeCennika(rodzaj),
      supabase
        .from('cennik_zmiany')
        .select('*')
        .not(kolumnaPozycji(rodzaj), 'is', null)
        .order('opublikowano_at', { ascending: false })
        .limit(50),
    ]);
    setPozycje(p);
    setZmiany((z.data ?? []) as ZmianaCennika[]);
  }, [rodzaj]);

  useEffect(() => {
    void czytaj();
  }, [czytaj]);

  const opublikuj = async () => {
    setBlad(null);
    const kwota = Number(form.kwota.replace(',', '.'));
    if (!form.pozycjaId || !Number.isFinite(kwota) || form.uzasadnienie.trim().length < 10) {
      setBlad('Wybierz pozycję, podaj kwotę netto i uzasadnienie (min. 10 znaków).');
      return;
    }
    setZapis(true);
    const { error } = await supabase.from('cennik_zmiany').insert({
      [kolumnaPozycji(rodzaj)]: form.pozycjaId,
      kwota_po: kwota,
      przyczyna: form.przyczyna,
      uzasadnienie: form.uzasadnienie.trim(),
      obowiazuje_od: new Date(`${form.obowiazujeOd}T00:00:00`).toISOString(),
    });
    setZapis(false);
    if (error) {
      setBlad(error.message);
      return;
    }
    setForm((f) => ({ ...f, pozycjaId: '', kwota: '', uzasadnienie: '' }));
    await czytaj();
  };

  const wycofaj = async (id: string) => {
    const { error } = await supabase
      .from('cennik_zmiany')
      .update({ wycofano_at: new Date().toISOString() })
      .eq('id', id);
    if (error) setBlad(error.message);
    else await czytaj();
  };

  return (
    <section
      className="card p-4 space-y-3"
      aria-label={rodzaj === 'plan' ? 'Zmiana cennika planów' : 'Zmiana cennika'}
    >
      <h2 className="card__title">
        {rodzaj === 'plan' ? 'Zmiana cennika planów' : 'Zmiana cennika'} (zawiadomienie 60 dni)
      </h2>
      <p className="text-sm text-aurora-muted">
        Kwoty pozycji, które ktoś płaci, nie zmienia się w edytorze — publikacja wysyła
        zawiadomienia, a kwota zmienia się sama w dniu obowiązywania, jeśli każdy płacący dostał je
        60 dni wcześniej.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <select
          className="input"
          aria-label="Pozycja cennika"
          value={form.pozycjaId}
          onChange={(e) => setForm({ ...form, pozycjaId: e.target.value })}
        >
          <option value="">Pozycja…</option>
          {pozycje.map((p) => (
            <option key={p.id} value={p.id}>
              {p.opis} · {kwotaZl(p.amount, p.currency)}
            </option>
          ))}
        </select>
        <input
          className="input"
          aria-label="Nowa kwota netto"
          placeholder="Nowa kwota netto"
          value={form.kwota}
          onChange={(e) => setForm({ ...form, kwota: e.target.value })}
        />
        <select
          className="input"
          aria-label="Przyczyna"
          value={form.przyczyna}
          onChange={(e) => setForm({ ...form, przyczyna: e.target.value })}
        >
          {Object.entries(PRZYCZYNY_ZMIANY).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <input
          className="input"
          type="date"
          aria-label="Obowiązuje od"
          value={form.obowiazujeOd}
          onChange={(e) => setForm({ ...form, obowiazujeOd: e.target.value })}
        />
      </div>
      <textarea
        className="input w-full"
        rows={2}
        placeholder="Uzasadnienie dla klientów"
        value={form.uzasadnienie}
        onChange={(e) => setForm({ ...form, uzasadnienie: e.target.value })}
      />
      {blad && <p className="text-sm text-red-400">{blad}</p>}
      <button className="btn btn--primary" disabled={zapis} onClick={() => void opublikuj()}>
        {zapis ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Opublikuj zmianę
      </button>
      {zmiany.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-aurora-muted">
              <th className="py-1 pr-2">Opublikowano</th>
              <th className="py-1 pr-2">Kwota</th>
              <th className="py-1 pr-2">Od</th>
              <th className="py-1 pr-2">Stan</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {zmiany.map((z) => (
              <tr key={z.id} className="border-t border-aurora-border">
                <td className="py-1 pr-2 mono">{dzien(z.opublikowano_at)}</td>
                <td className="py-1 pr-2 mono">
                  {kwotaZl(z.kwota_przed, z.waluta)} → {kwotaZl(z.kwota_po, z.waluta)}
                </td>
                <td className="py-1 pr-2 mono">{dzien(z.obowiazuje_od)}</td>
                <td className="py-1 pr-2">
                  {z.wycofano_at ? 'wycofana' : z.zastosowano_at ? 'zastosowana' : 'czeka na datę'}
                </td>
                <td className="py-1">
                  {!z.wycofano_at && !z.zastosowano_at && (
                    <button className="btn btn--ghost" onClick={() => void wycofaj(z.id)}>
                      Wycofaj
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function LicznikPoziomuBezplatnego() {
  const [stan, setStan] = useState<{
    konta: number;
    z_nip: number;
    bez_nip: number;
    sufit: number;
    wolne: number;
  } | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  useEffect(() => {
    let zywy = true;
    void (async () => {
      try {
        const { data, error } = await supabase.rpc('poziom_bezplatny_stan');
        if (!zywy) return;
        if (error) setBlad(error.message);
        else setStan(((data ?? []) as Array<typeof stan>)[0] ?? null);
      } catch (e) {
        // Odczyt, który rzucił, to też odpowiedź — nie znika po cichu.
        if (zywy) setBlad(e instanceof Error ? e.message : 'nieznany błąd odczytu');
      }
    })();
    return () => {
      zywy = false;
    };
  }, []);
  if (blad) return <p className="text-sm text-red-400">Poziom bezpłatny: {blad}</p>;
  if (!stan) return null;
  return (
    <p className="text-sm text-aurora-muted" aria-label="Konta bezpłatne">
      Konta bezpłatne: <span className="mono">{stan.konta}</span> z{' '}
      <span className="mono">{stan.sufit}</span> (wolne <span className="mono">{stan.wolne}</span>;
      bez NIP-u <span className="mono">{stan.bez_nip}</span>)
    </p>
  );
}
